import { get_color } from "../../../report-visuals/report-utils";
import { TARS_PIVOT_TOTAL_LABEL } from "../tars-constants";
import {
    isColorByLabelMap,
    parseNumericCellValue,
    parseOptionalColorHex,
    resolveHexFromColorByLabel,
    type TARSChartConfiguration,
} from "./tarsBarChartHelpers";

const TARS_STACKED_BAR_PERCENT_DECIMAL_PLACES = 1;

export type TARSStackedBarChartTableData = {
    rows: unknown[][];
    headers: unknown[];
    preserveRowOrder?: boolean;
    title?: string;
    /** Optional #hex per value column (headers[1]…), same order as stack segments. */
    seriesColors?: unknown[];
    /** Facet chart `configuration`; `colorByLabel` keys match stack segment header names. */
    configuration?: TARSChartConfiguration;
};

export type TARSStackedBarSeriesDef = {
    name: string;
    valueField: string;
    fillHex: string;
};

export type TARSStackedBarChartModel = {
    chartData: Array<Record<string, string | number>>;
    series: TARSStackedBarSeriesDef[];
};

function stackedNumericOrZero(cellValue: unknown): number {
    const n = parseNumericCellValue(cellValue);
    return n ?? 0;
}

function isPivotTotalLabel(value: unknown): boolean {
    return String(value ?? "").trim().toLowerCase() === TARS_PIVOT_TOTAL_LABEL.toLowerCase();
}

/**
 * Table shape: headers[0] = category; headers[1..] = one stack segment each (numeric cells).
 *
 * **100% stacked:** each row is normalized so `v0`…`vN` are shares of `rowTotal` in **0–100**
 * (stacked length always 100%). `raw0`…`rawN` keep the source numbers for tooltips and labels.
 */
export function rowsToTARSStackedBarChartModel(
    data: TARSStackedBarChartTableData
): TARSStackedBarChartModel | null {
    const rawHeaders = Array.isArray(data.headers) ? data.headers : [];
    const rawRows = Array.isArray(data.rows) ? data.rows : [];
    if (rawHeaders.length < 2 || rawRows.length === 0) return null;

    const keptColIndices: number[] = [0];
    for (let j = 1; j < rawHeaders.length; j++) {
        if (!isPivotTotalLabel(rawHeaders[j])) keptColIndices.push(j);
    }
    if (keptColIndices.length < 2) return null;

    const columnHeaders = keptColIndices.map((i) => rawHeaders[i]);
    const rawSeriesColors = Array.isArray(data.seriesColors) ? data.seriesColors : null;
    const seriesColors = rawSeriesColors
        ? keptColIndices.slice(1).map((colIdx) => rawSeriesColors[colIdx - 1])
        : null;
    const colorByLabelRaw = data.configuration?.colorByLabel;
    const colorByLabel = isColorByLabelMap(colorByLabelRaw) ? colorByLabelRaw : undefined;
    const valueColumnCount = columnHeaders.length - 1;
    const series: TARSStackedBarSeriesDef[] = [];
    for (let j = 0; j < valueColumnCount; j++) {
        const name =
            columnHeaders[j + 1] != null && String(columnHeaders[j + 1]).trim() !== ""
                ? String(columnHeaders[j + 1])
                : `Series ${j + 1}`;
        const valueField = `v${j}`;
        const fillHex =
            parseOptionalColorHex(seriesColors?.[j]) ??
            resolveHexFromColorByLabel(colorByLabel, name) ??
            get_color(j);
        series.push({ name, valueField, fillHex });
    }

    const chartData: Array<Record<string, string | number>> = [];
    rawRows.forEach((rowCells) => {
        if (!Array.isArray(rowCells) || rowCells.length < 2) return;
        if (isPivotTotalLabel(rowCells[0])) return;

        const catRaw = rowCells[keptColIndices[0]];
        const label = catRaw != null && String(catRaw).trim() !== "" ? String(catRaw) : "—";
        const row: Record<string, string | number> = { label };

        const rawByIndex: number[] = [];
        for (let j = 0; j < valueColumnCount; j++) {
            rawByIndex[j] = stackedNumericOrZero(rowCells[keptColIndices[j + 1]]);
        }
        const sum = rawByIndex.reduce((a, b) => a + b, 0);
        if (sum <= 0) return;
        row.rowTotal = sum;

        for (let j = 0; j < valueColumnCount; j++) {
            const raw = rawByIndex[j];
            row[`raw${j}`] = raw;
            const field = `v${j}`;
            row[field] = sum > 0 ? (raw / sum) * 100 : 0;
            const pct = sum > 0 ? (raw / sum) * 100 : 0;
            row[`pctLabel${j}`] = `${pct.toFixed(TARS_STACKED_BAR_PERCENT_DECIMAL_PLACES)} %`;
        }
        chartData.push(row);
    });

    if (chartData.length === 0) return null;
    if (!data.preserveRowOrder) {
        chartData.sort((a, b) => (Number(b.rowTotal) || 0) - (Number(a.rowTotal) || 0));
    }

    return { chartData, series };
}

export function applyDynamicCornerRadius(colTpl: any, series: any[], seriesIndex: number) {
    const getValue = (data: any, idx: number) => {
        return data?.[series[idx].valueField] ?? 0;
    };

    const isFirstVisible = (data: any) => {
        for (let i = 0; i < seriesIndex; i++) {
            if (getValue(data, i) > 0) return false;
        }
        return getValue(data, seriesIndex) > 0;
    };

    const isLastVisible = (data: any) => {
        for (let i = series.length - 1; i > seriesIndex; i--) {
            if (getValue(data, i) > 0) return false;
        }
        return getValue(data, seriesIndex) > 0;
    };

    const radius = 10;

    colTpl.adapters.add("cornerRadiusTL", (_: any, target: any) => {
        const data = target.dataItem?.dataContext;
        return isFirstVisible(data) ? radius : 0;
    });

    colTpl.adapters.add("cornerRadiusBL", (_: any, target: any) => {
        const data = target.dataItem?.dataContext;
        return isFirstVisible(data) ? radius : 0;
    });

    colTpl.adapters.add("cornerRadiusTR", (_: any, target: any) => {
        const data = target.dataItem?.dataContext;
        return isLastVisible(data) ? radius : 0;
    });

    colTpl.adapters.add("cornerRadiusBR", (_: any, target: any) => {
        const data = target.dataItem?.dataContext;
        return isLastVisible(data) ? radius : 0;
    });
}
